import { SxProps, Theme } from '@mui/material';

export const headerBoxStyles: SxProps<Theme> = {
    mb: 3,
};

/**
 * Book Appointment, pinned below the app bar so it stays reachable no matter how far
 * down the upcoming/past lists the page has scrolled -- the header's own "Book Appointment"
 * nav entry is easy to miss, and this is the more visible stand-in for it.
 *
 * Same offset and frosted-pill treatment as the sticky Save/Cancel bar on OurServices and
 * About's editors, so a floating action reads the same wherever it shows up.
 */
export const floatingBookActionStyles: SxProps<Theme> = {
    display: 'flex',
    justifyContent: 'center',
    mb: 3,
    py: 1.5,
    px: 2,
    position: 'sticky',
    top: 72,
    zIndex: 10,
    backgroundColor: 'rgba(255,255,255,0.85)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderRadius: 999,
    border: '1px solid rgba(16,24,40,0.06)',
};

export const titleStyles: SxProps<Theme> = {
    fontWeight: 700,
};

export const subtitleStyles: SxProps<Theme> = {
    color: 'text.secondary',
};

export const sectionTitleStyles: SxProps<Theme> = {
    fontWeight: 600,
    mt: 4,
    mb: 1.5,
};

export const listStyles: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
};

/**
 * One appointment.
 *
 * `isActionable` is what a therapist sees: the row opens the detail dialog, so it has to
 * look like something that can be pressed. A patient's copy is the same row without the
 * affordance, because every action on this page needs a role the API will not grant them.
 */
export const rowStyles = (past: boolean, isActionable: boolean): SxProps<Theme> => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 2,
    flexWrap: 'wrap',
    p: 2,
    borderRadius: 2,
    border: '1px solid rgba(16,24,40,0.08)',
    backgroundColor: 'background.paper',
    // Past appointments are still readable, just visibly done with.
    opacity: past ? 0.7 : 1,
    ...(isActionable
        ? {
            cursor: 'pointer',
            transition: 'border-color 120ms, box-shadow 120ms',
            '&:hover': {
                borderColor: 'var(--ch-primary)',
                boxShadow: '0 1px 6px rgba(16,24,40,0.08)',
            },
            '&:focus-visible': {
                outline: '2px solid var(--ch-primary)',
                outlineOffset: 2,
            },
        }
        : {}),
});

/**
 * The status dropdown over the past list.
 *
 * Over that list alone, because "upcoming" is unresolved by definition -- a status filter
 * on it would only ever have one answer. It narrows in SQL, so what it shows is what the
 * clinic holds rather than what this page happened to download.
 */
export const statusFieldStyles: SxProps<Theme> = {
    minWidth: 180,
    mb: 2,
};

/** The "show more" that fetches the next page, one per list. */
export const showMoreBoxStyles: SxProps<Theme> = {
    display: 'flex',
    justifyContent: 'center',
    mt: 2,
};

export const whenStyles: SxProps<Theme> = {
    fontWeight: 600,
};

export const withStyles: SxProps<Theme> = {
    color: 'text.secondary',
    fontSize: '0.85rem',
};

export const emptyStateStyles: SxProps<Theme> = {
    py: 4,
    color: 'text.secondary',
};

export const loadingBoxStyles: SxProps<Theme> = {
    display: 'flex',
    justifyContent: 'center',
    py: 6,
};

export const statusChipStyles: SxProps<Theme> = {
    ml: 1,
};

/**
 * The patient's *Change time*, sitting at the end of its row.
 *
 * `flexShrink: 0` because the row is a flex line whose left half is two lines of text that
 * will happily consume everything: without it the label wraps mid-word long before the row
 * is narrow enough to need it. The row itself already carries `flexWrap`, so on a phone
 * this drops underneath the text rather than squeezing beside it.
 */
export const rowActionStyles: SxProps<Theme> = {
    flexShrink: 0,
};

/** A pending request's row carries two, so they sit together and wrap together. */
export const rowActionsStyles: SxProps<Theme> = {
    display: 'flex',
    gap: 1,
    flexWrap: 'wrap',
    flexShrink: 0,
};

/** The line under a request or a decline saying where it stands: whose answer, by when, or why. */
export const requestNoteStyles: SxProps<Theme> = {
    color: 'text.secondary',
    fontSize: '0.8rem',
    mt: 0.25,
};
